import type { TPhotoValue } from '@src/models/interfaces/components/photoComponent.interface';
import WorkOrderPhotoRepository, {
  WorkOrderPhotoUploadParams,
} from '@src/repositories/WorkOrderPhotoRepository';

const BYTES_PER_KILOBYTE = 1024;

export const getPhotoSizeInKilobytes = (sizeInBytes: number): number =>
  Math.ceil(sizeInBytes / BYTES_PER_KILOBYTE);

export interface WorkOrderPhotoUploader {
  upload(params: WorkOrderPhotoUploadParams): Promise<TPhotoValue>;
  remove(photo: TPhotoValue): Promise<void>;
}

class WorkOrderPhotoService implements WorkOrderPhotoUploader {
  private readonly repository: WorkOrderPhotoRepository;

  constructor(repository = new WorkOrderPhotoRepository()) {
    this.repository = repository;
  }

  async upload(params: WorkOrderPhotoUploadParams): Promise<TPhotoValue> {
    const url = await this.repository.upload(params);

    return {
      size: getPhotoSizeInKilobytes(params.file.size),
      tags: [],
      url,
    };
  }

  async remove(photo: TPhotoValue): Promise<void> {
    await this.repository.remove(photo.url);
  }
}

export const workOrderPhotoService = new WorkOrderPhotoService();

/**
 * Tracks photos uploaded while a work order is being edited so the ones that never get
 * saved can be deleted from Storage when the user leaves the form.
 */
export class WorkOrderPhotoUploadSession implements WorkOrderPhotoUploader {
  private readonly unsavedPhotos = new Map<string, TPhotoValue>();

  private readonly inFlightUploads = new Set<Promise<TPhotoValue>>();

  private readonly pendingSaves = new Set<Promise<unknown>>();

  private readonly uploader: WorkOrderPhotoUploader;

  constructor(uploader: WorkOrderPhotoUploader = workOrderPhotoService) {
    this.uploader = uploader;
  }

  async upload(params: WorkOrderPhotoUploadParams): Promise<TPhotoValue> {
    const upload = this.uploader.upload(params);
    this.inFlightUploads.add(upload);

    try {
      const photo = await upload;
      this.unsavedPhotos.set(photo.url, photo);
      return photo;
    } finally {
      this.inFlightUploads.delete(upload);
    }
  }

  async remove(photo: TPhotoValue): Promise<void> {
    await this.uploader.remove(photo);
    this.unsavedPhotos.delete(photo.url);
  }

  markSaved(photos: TPhotoValue[]): void {
    photos.forEach(photo => this.unsavedPhotos.delete(photo.url));
  }

  /**
   * Marks `photos` as saved once `save` resolves. Until then, `discardUnsaved` waits for it so
   * leaving the form mid-save cannot delete photos the saved record points to.
   */
  async trackSave<T>(save: Promise<T>, photos: TPhotoValue[]): Promise<T> {
    this.pendingSaves.add(save);

    try {
      const result = await save;
      this.markSaved(photos);
      return result;
    } finally {
      this.pendingSaves.delete(save);
    }
  }

  async discardUnsaved(): Promise<void> {
    await Promise.allSettled(Array.from(this.pendingSaves));

    // Uploads still in flight are removed one by one as they land, so a stalled upload does not
    // hold up deleting the photos that already finished.
    const lateUploads = Array.from(this.inFlightUploads, upload =>
      upload.then(photo => this.removeUnsaved(photo)),
    );
    const finishedPhotos = Array.from(this.unsavedPhotos.values());
    await Promise.allSettled([
      ...finishedPhotos.map(photo => this.removeUnsaved(photo)),
      ...lateUploads,
    ]);
  }

  private async removeUnsaved(photo: TPhotoValue): Promise<void> {
    this.unsavedPhotos.delete(photo.url);
    await this.uploader.remove(photo);
  }
}

export default WorkOrderPhotoService;
